import { useCallback, useContext, useEffect, useMemo, useState } from "react";
import { AuthContext } from "../../../contexts/AuthContext";
import PageTitle from "../../../components/PageTitle/PageTitle";
import "./MyChatMessages.scss";
import AlertModal from "../../../components/AlertModal/AlertModal";
import { DynamicTextarea } from "../../../components/Inputs/Inputs";
import { Modal } from "../../../components/Modal/Modal";
import { CiEdit } from "react-icons/ci";
import Table from "../../../components/Table/Table";

const MESSAGE_MAX_LENGTH = 500;

const DEFAULT_TITLES = [
  "Mensagem inicial 1",
  "Mensagem inicial 2",
  "Mensagem reservar estadia",
  "Mensagem selecionar propriedade",
  "Mensagem selecionar restaurante",
  "Mensagem selecionar sala de eventos",
  "Mensagem escolher tópico",
  "Mensagem preencher formulário",
  "Mensagem de incentivo (inatividade do visitante)",
];

const emptyForm = {
  id: null,
  message_pt: "",
  message_en: "",
  message_es: "",
};

export default function MyChatMessages() {
  const { axiosAuth, accessibleGroups = [] } = useContext(AuthContext);

  // group_admin tem 1 grupo
  const groupId = useMemo(
    () => (accessibleGroups.length ? String(accessibleGroups[0].id) : ""),
    [accessibleGroups]
  );

  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);

  const [errorModal, setErrorModal] = useState({
    open: false,
    title: "",
    message: "",
  });

  const [isEditOpen, setIsEditOpen] = useState(false);
  const [editForm, setEditForm] = useState(emptyForm);

  const openError = useCallback((title, message) => {
    setErrorModal({ open: true, title, message });
  }, []);

  const closeError = useCallback(
    () => setErrorModal({ open: false, title: "", message: "" }),
    []
  );

  // carregar mensagens fixas do grupo
  const fetchMessages = useCallback(async () => {
    if (!groupId) {
      setMessages([]);
      return;
    }
    setLoading(true);
    try {
      const { data } = await axiosAuth.get(
        `/chat-fixed-messages/group/${groupId}`
      );
      // ordem estável por id asc -> corresponde ao papel lógico usado pelo bot
      const list = Array.isArray(data)
        ? [...data].sort((a, b) => a.id - b.id)
        : [];
      setMessages(list);
    } catch (err) {
      openError(
        "Erro ao carregar mensagens",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setLoading(false);
    }
  }, [groupId, axiosAuth, openError]);

  useEffect(() => {
    fetchMessages();
  }, [fetchMessages]);

  // helpers UI
  const truncate = (s = "", n = 60) => (s.length > n ? `${s.slice(0, n)}…` : s);

  const titleFor = useCallback(
    (id) => {
      const idx = messages.findIndex((m) => m.id === id);
      return DEFAULT_TITLES[idx] || `Mensagem ${id}`;
    },
    [messages]
  );

  // editar
  const handleOpenEdit = useCallback((m) => {
    setEditForm({
      id: m.id,
      message_pt: m.message_pt || "",
      message_en: m.message_en || "",
      message_es: m.message_es || "",
    });
    setIsEditOpen(true);
  }, []);

  const updateEdit = useCallback((name, value) => {
    setEditForm((p) => ({ ...p, [name]: value }));
  }, []);

  const handleSubmitEdit = useCallback(async () => {
    if (!editForm.id) return;

    const required = ["message_pt", "message_en", "message_es"];
    for (const f of required) {
      if (!editForm[f]?.trim()) {
        openError("Validação", "Preencha todos os campos obrigatórios.");
        return;
      }
    }

    setSaving(true);
    try {
      await axiosAuth.patch(`/chat-fixed-messages/${editForm.id}`, {
        message_pt: editForm.message_pt,
        message_en: editForm.message_en,
        message_es: editForm.message_es,
      });
      setIsEditOpen(false);
      await fetchMessages();
    } catch (err) {
      openError(
        "Erro ao guardar mensagem",
        err?.response?.data?.detail || err.message
      );
    } finally {
      setSaving(false);
    }
  }, [editForm, axiosAuth, fetchMessages, openError]);

  // tabela
  const columns = useMemo(
    () => [
      { key: "title", label: "Mensagem" },
      { key: "message_pt_short", label: "Texto (PT)" },
      {
        key: "actions",
        label: "Ações",
        render: (row) => (
          <button
            className="action-button edit"
            onClick={(e) => {
              e.stopPropagation();
              handleOpenEdit(row._raw);
            }}
            title="Editar"
          >
            <CiEdit />
          </button>
        ),
      },
    ],
    [handleOpenEdit]
  );

  const tableData = useMemo(
    () =>
      messages.map((m) => ({
        id: m.id,
        title: titleFor(m.id),
        message_pt_short: truncate(m.message_pt || "", 60),
        _raw: m,
      })),
    [messages, titleFor]
  );

  return (
    <div className="my-chat-messages">
      {errorModal.open && (
        <AlertModal
          variant="danger"
          title={errorModal.title || "Ocorreu um erro"}
          message={errorModal.message}
          onClose={closeError}
        />
      )}

      <PageTitle link="" title="Mensagens fixas do chat" />

      <div className="container">
        {!loading && tableData.length === 0 && (
          <div className="empty">Sem mensagens para este grupo.</div>
        )}
        <Table columns={columns} data={tableData} />
      </div>

      {/* modal de edição */}
      <Modal
        isVisible={isEditOpen}
        onClose={() => setIsEditOpen(false)}
        title="Editar mensagem"
        saveText="Guardar"
        onSave={handleSubmitEdit}
        disableActions={saving}
      >
        <div className="row">
          <DynamicTextarea
            label="Mensagem PT"
            name="message_pt"
            rows={4}
            required
            maxLength={MESSAGE_MAX_LENGTH}
            value={editForm.message_pt}
            onChange={(_, v) => updateEdit("message_pt", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Mensagem EN"
            name="message_en"
            rows={4}
            required
            maxLength={MESSAGE_MAX_LENGTH}
            value={editForm.message_en}
            onChange={(_, v) => updateEdit("message_en", v)}
          />
        </div>
        <div className="row">
          <DynamicTextarea
            label="Mensagem ES"
            name="message_es"
            rows={4}
            required
            maxLength={MESSAGE_MAX_LENGTH}
            value={editForm.message_es}
            onChange={(_, v) => updateEdit("message_es", v)}
          />
        </div>
      </Modal>
    </div>
  );
}
